import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import BasicButton from "@/src/components/atom/Button/BasicButton";
import Typo from "@/src/components/atom/Typo";
import { InputError } from "@/src/components/molecule/FormItem";
import PublicPageHeader from "@/src/components/organism/PublicPageHeader";
import PublicPageFooter from "@/src/components/molecule/PublicPageFooter";
import { useSendContactUsMutation } from "@/src/store/apis/inquiry";
import { SendContactUsDto } from "@/src/store/apis/inquiry/interface";
import PUBLIC_PATH from "@/src/routes/public/path";
import { notification } from "antd";
import colorSet from "@/src/styles/color";

interface ContactUsFormData extends SendContactUsDto {}

const ContactUsPage: React.FC = () => {
  const { t, i18n } = useTranslation(["contactUs", "error"]);
  const navigate = useNavigate();

  useEffect(() => {
    i18n.changeLanguage("ko");
  }, [i18n]);
  const [sendContactUs, { isLoading }] = useSendContactUsMutation();
  const [contentLength, setContentLength] = useState(0);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ContactUsFormData>({
    mode: "onBlur",
    defaultValues: {
      companyName: "",
      name: "",
      email: "",
      content: "",
      marketingConsent: false,
      language: i18n.language,
    },
  });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const watchEmail = watch("email");
  const isEmailValid = watchEmail && validateEmail(watchEmail);
  const isButtonEnabled = isEmailValid;

  const onSubmit = async (data: ContactUsFormData) => {
    try {
      // 언어 변경 시 최신 언어 값을 사용하도록 i18n.language를 직접 전달
      await sendContactUs({
        ...data,
        language: i18n.language,
      }).unwrap();
      navigate(PUBLIC_PATH.CONTACT_US_SUCCESS);
    } catch (error) {
      notification.error({
        message: t("error:contactUs.submitFailed"),
      });
    }
  };

  return (
    <Layout>
      <ContactUsSection>
        <PublicPageHeader />

        <Container>
          <HeaderSection>
            <TitleWrapper>
              <Typo typoType="d3" color="gray1">
                {t("contactUs:pageTitle")}
              </Typo>
            </TitleWrapper>
            <SubtitleWrapper>
              <Typo typoType="d6" color="gray2">
                {t("contactUs:pageSubtitle")}
              </Typo>
            </SubtitleWrapper>
            <DescriptionWrapper>
              <Typo typoType="b6r" color="gray4">
                {t("contactUs:pageDescription")}
                {"\n"}
                {"\n"}
              </Typo>
            </DescriptionWrapper>
          </HeaderSection>

          <FormWrapper>
            <FormContent onSubmit={handleSubmit(onSubmit)}>
              <FormField>
                <Label>
                  <Typo typoType="b7m" color="gray2">
                    {t("contactUs:companyNameLabel")}
                  </Typo>
                </Label>
                <Controller
                  name="companyName"
                  control={control}
                  rules={{ maxLength: 200 }}
                  render={({ field }) => (
                    <StyledInput
                      {...field}
                      type="text"
                      placeholder={t("contactUs:companyNamePlaceholder")}
                      $hasError={!!errors.companyName}
                    />
                  )}
                />
              </FormField>

              <FormField>
                <Label>
                  <Typo typoType="b7m" color="gray2">
                    {t("contactUs:nameLabel")}
                  </Typo>
                </Label>
                <Controller
                  name="name"
                  control={control}
                  rules={{ maxLength: 100 }}
                  render={({ field }) => (
                    <StyledInput
                      {...field}
                      type="text"
                      placeholder={t("contactUs:namePlaceholder")}
                      $hasError={!!errors.name}
                    />
                  )}
                />
              </FormField>

              <FormField>
                <Label>
                  <Typo typoType="b7m" color="gray2">
                    {t("contactUs:emailLabel")}
                  </Typo>
                  <RequiredMark>
                    <Typo typoType="b7m" color="red2">
                      *
                    </Typo>
                  </RequiredMark>
                </Label>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: true,
                    validate: validateEmail,
                    maxLength: 200,
                  }}
                  render={({ field }) => (
                    <StyledInput
                      {...field}
                      type="text"
                      placeholder={t("contactUs:emailPlaceholder")}
                      $hasError={!!errors.email}
                    />
                  )}
                />
                {errors.email && (
                  <InputError
                    message={
                      <>
                        {errors.email.type === "required" && t("error:required")}
                        {errors.email.type === "validate" &&
                          t("error:login.emailTypeMissMatching")}
                      </>
                    }
                  />
                )}
              </FormField>

              <FormField>
                <Label>
                  <Typo typoType="b7m" color="gray2">
                    {t("contactUs:contentLabel")}
                  </Typo>
                </Label>
                <Controller
                  name="content"
                  control={control}
                  rules={{ maxLength: 100 }}
                  render={({ field }) => (
                    <TextareaWrapper>
                      <StyledTextarea
                        {...field}
                        placeholder={t("contactUs:contentPlaceholder")}
                        maxLength={100}
                        onChange={(e) => {
                          field.onChange(e);
                          setContentLength(e.target.value.length);
                        }}
                        $hasError={!!errors.content}
                      />
                      <CharCounter>
                        <Typo typoType="label2" color="gray7">
                          {contentLength} / 100
                        </Typo>
                      </CharCounter>
                    </TextareaWrapper>
                  )}
                />
              </FormField>

            </FormContent>

            <ButtonWrapper>
              <BasicButton
                type="submit"
                buttonGrade="primary"
                buttonColor="blue"
                buttonSize={40}
                isLoading={isLoading}
                disabled={!isButtonEnabled || isLoading}
                onClick={handleSubmit(onSubmit)}
              >
                {t("contactUs:submitButton")}
              </BasicButton>
            </ButtonWrapper>
          </FormWrapper>
        </Container>

        <PublicPageFooter />
      </ContactUsSection>
    </Layout>
  );
};

export default ContactUsPage;

const Layout = styled.main`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
`;

const ContactUsSection = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  padding: 60px 40px;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  padding: 0 40px;
`;

const TitleWrapper = styled.div`
  padding: 24px 0;
`;

const SubtitleWrapper = styled.div`
  padding: 8px 0;
`;

const DescriptionWrapper = styled.div`
  padding: 8px 0;
  text-align: center;
  white-space: pre-line;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 520px;
`;

const FormContent = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const RequiredMark = styled.span`
  display: inline-flex;
`;

const StyledInput = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  background-color: ${colorSet.white};
  border: 1px solid ${(props) => (props.$hasError ? colorSet.red2 : colorSet.gray9)};
  border-radius: 8px;
  padding: 8px 10px 8px 16px;
  font-family: "Pretendard", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: ${colorSet.gray2};

  &::placeholder {
    color: ${colorSet.gray7};
  }

  &:focus {
    outline: none;
    border-color: ${colorSet.blue5};
  }
`;

const TextareaWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledTextarea = styled.textarea<{ $hasError?: boolean }>`
  width: 100%;
  height: 156px;
  background-color: ${colorSet.white};
  border: 1px solid ${(props) => (props.$hasError ? colorSet.red2 : colorSet.gray9)};
  border-radius: 8px;
  padding: 10px 16px;
  font-family: "Pretendard", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: ${colorSet.gray2};
  resize: none;

  &::placeholder {
    color: ${colorSet.gray7};
  }

  &:focus {
    outline: none;
    border-color: ${colorSet.blue5};
  }
`;

const CharCounter = styled.div`
  position: absolute;
  bottom: 12px;
  right: 6px;
  transform: translateY(50%);
`;

const CheckboxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const ErrorMessageWrapper = styled.div`
  padding-left: 28px;
  width: 100%;
`;

const ButtonWrapper = styled.div`
  width: 100%;

  button {
    width: 100%;
  }
`;
